import { useCallback } from "react";

import { t } from "i18n/i18n";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectIsVirtualUserPresentInClass,
  selectParticipantBarFeaturedClassUserIds,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";

const MAX_FEATURED_PARTICIPANT_BAR_SLOTS = 3;

export const useFeatureParticipantTools = (classUserId = "") => {
  const dispatch = useAppDispatch();
  const currentFeaturedClassUserIds = useAppSelector(selectParticipantBarFeaturedClassUserIds);
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const participantBarVisibleClassUserIds = useAppSelector(selectParticipantBarVisibleClassUserIds);

  const onFeatureClassUserId = useCallback(() => {
    if (!classUserId) return;

    if (currentFeaturedClassUserIds.some((featuredUser) => featuredUser.id === classUserId)) {
      return;
    }
    // We slice here, this means we bring back the cap if a group is featured
    const newFeaturedClassUserIds = [
      { id: classUserId, timestamp: Date.now() },
      ...currentFeaturedClassUserIds,
    ].slice(0, MAX_FEATURED_PARTICIPANT_BAR_SLOTS);

    dispatch(
      PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds(newFeaturedClassUserIds),
    );
  }, [dispatch, classUserId, currentFeaturedClassUserIds]);

  const onFeatureClassUserIdList = useCallback(
    (newClassUserIds: string[]) => {
      const featuredClassUserIds = newClassUserIds.map((id) => ({ id, timestamp: Date.now() }));
      dispatch(
        PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds(featuredClassUserIds),
      );
    },
    [dispatch],
  );

  const isParticipantVisibleInBar =
    classUserId != null && participantBarVisibleClassUserIds.includes(classUserId);

  const featureParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.view_participant_not_available")
    : isParticipantVisibleInBar
      ? t("user_options_menu.view_participant_already_visible")
      : undefined;

  return { onFeatureClassUserId, onFeatureClassUserIdList, featureParticipantDisabledText };
};
